<template>
  <section class="flex product-banner pb20 container px20 c-white flex bottom-xs">
    <div
      v-if="prevRoute"
      class="visible-mobile hidden-tablet hidden-desktop back-button absolute pointer c-white"
      @click="handleBack"
    >
      <i class="icon icon-arrow-back" />
    </div>
    <div class="visible-mobile hidden-tablet row m0 center-xs absolute left-0 top-0 w-100 px65">
      <span class="c-white uppercase fs25 mt20 page-header">
        {{ categoryName }}
      </span>
    </div>
    <div class="row m0 align-left">
      <h1 class="page-title shadow-text mb10 c-white w-100">
        <span>{{ productHeader }}</span>
      </h1>
      <div class="mb5">
        <div
          v-if="productLocation"
          class="row mt2 w-100 m0 attribute-wrapper"
        />
        <div class="information information-container mr15 fs16 flex align-items-baseline">
          <i class="icon icon-marker-white mr10" />
          <h2 class="information shadow-text m0 p0">
            {{ productLocation }}
          </h2>
        </div>
        <div
          v-if="productDate"
          class="row mt2 w-100 m0 attribute-wrapper"
        />
        <div class="information information-container mr15 fs16 flex align-items-baseline">
          <i class="icon icon-schedule-white mr10" />
          <h2 class="information shadow-text m0 p0">
            {{ productDate }}
          </h2>
        </div>
        <div
          v-if="productPriceMargin"
          class="row mt2 w-100 m0 attribute-wrapper"
        />
        <div class="information information-container mr15 fs16 flex align-items-baseline">
          <i class="icon icon-tickets-white mr10" />
          <h2 class="information shadow-text m0 p0">
            {{ productPriceMargin }}
          </h2>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import filterForDate from 'theme/mixins/filterForDate.ts'
import config from 'config'
import { isCheckoutPath } from 'src/modules/ab-test/helpers'

export default {
  name: 'ProductBanner',
  components: {},
  mixins: [filterForDate],
  props: {
    product: {
      type: Object,
      default: () => {
      }
    }
  },
  computed: {
    ...mapState({
      prevRouteState: state => state.url.prevRoute.path
    }),
    ...mapGetters({
      getAttributesByCode: 'attribute/attributeListByCode',
      getCategories: 'category-next/getMenuCategories'
    }),
    prevRoute () {
      if (this.prevRouteState && !isCheckoutPath(this.prevRouteState, config.abTest?.checkout || {})) {
        return this.prevRouteState
      }

      return this.product.category ? this.product.category[0].url : null
    },
    artistCategory () {
      if (!Array.isArray(this.product.category) || !this.product.category.length) {
        return null
      }

      const productCategoryIds = this.product.category
        .map(category => Number(category?.category_id))
        .filter(categoryId => !Number.isNaN(categoryId) && categoryId > 0)
      const fallbackCategory = this.product.category[0]

      if (!Array.isArray(this.getCategories) || !this.getCategories.length || !productCategoryIds.length) {
        return fallbackCategory
      }

      const artistCategory = this.getCategories.find(category =>
        category.level === 5 &&
        (category.is_artist === true) &&
        productCategoryIds.includes(Number(category.id))
      )

      return artistCategory || fallbackCategory
    },
    categoryName () {
      return this.artistCategory ? this.artistCategory.name : ''
    },
    eventDate () {
      return this.parseDate(this.product.date, this.$t('at'))
    },
    cityAttribute () {
      if (this.getAttributesByCode.city) {
        return this.getAttributesByCode.city.options.find(option => Number(option.value) === this.product.city ? option : '')
      }
      return false
    },
    locationAttribute () {
      if (this.getAttributesByCode.location) {
        return this.getAttributesByCode.location.options.find(option => Number(option.value) === this.product.location ? option : '')
      }
      return false
    },
    productHeader () {
      return this.$t('Tickets for {artist} in {city} {date}',
        {
          artist: this.categoryName,
          city: this.cityAttribute ? this.cityAttribute.label : '',
          date: this.eventDate
        }
      )
    },
    productLocation () {
      if (this.cityAttribute || this.locationAttribute) {
        const cityLabel = this.cityAttribute ? this.cityAttribute.label : ''
        const locationLabel = this.locationAttribute ? this.locationAttribute.label : ''

        return this.$t('{artist} live in {location} {city}',
          {
            artist: this.categoryName,
            city: cityLabel,
            location: locationLabel
          }
        )
      }
      return false
    },
    productDate () {
      if (this.eventDate && this.categoryName && this.product.category && this.product.category[0]) {
        switch (this.product.category[0].category_name) {
          case 'Konsert':
            return this.$t('Concert with {artist} takes place on {date}',
              {
                artist: this.categoryName,
                date: this.eventDate
              }
            )
          case 'Humor':
          case 'Teater':
            return this.$t('Performance {artist} takes place on {date}',
              {
                artist: this.categoryName,
                date: this.eventDate
              }
            )
          case 'Sport':
          case 'Festival':
          case 'Krogshow':
            return this.$t('Event {artist} takes place on {date}',
              {
                artist: this.categoryName,
                date: this.eventDate
              }
            )
          default:
            return this.$t('{artist} {type} takes place on {date}',
              {
                artist: this.categoryName,
                date: this.eventDate,
                type: this.product.category[0].category_name.toLowerCase()
              }
            )
        }
      }

      return false
    },
    getProductPriceMargin () {
      if (this.product.max_price && this.product.min_price) {
        return { maxPrice: this.product.max_price, minPrice: this.product.min_price }
      } else {
        return { ticketPrice: this.product.final_price_incl_tax }
      }
    },
    productPriceMargin () {
      if (this.getProductPriceMargin) {
        const priceMargin = this.getProductPriceMargin
        if (priceMargin.ticketPrice) {
          return this.$t('The tickets to {artist} cost {ticketPrice} euro',
            {
              artist: this.categoryName,
              ticketPrice: priceMargin.ticketPrice
            }
          )
        }
        return this.$t('Tickets to {artist} cost between {minPrice}-{maxPrice} euro',
          {
            artist: this.categoryName,
            minPrice: priceMargin.minPrice,
            maxPrice: priceMargin.maxPrice
          }
        )
      }
      return false
    }
  },
  methods: {
    handleBack () {
      if (this.prevRouteState && !isCheckoutPath(this.prevRouteState, config.abTest?.checkout || {})) {
        this.$router.back()
        return
      }

      if (this.prevRoute) {
        this.$router.replace(this.prevRoute)
      }
    }
  }
}
</script>
<style lang="scss" scoped>
@import '~theme/css/variables/colors';
@import '~theme/css/helpers/functions/color';
@import '~theme/css/variables/grid';
@import '~theme/css/helpers/functions/z-index-selectors';

.product-banner {
  h1 {
    font-size: 20px;
  }

  span.page-header {
    font-weight: bold;
    font-size: 16px;
  }

  z-index: zIndex(banner);

  .back-button {
    top: 20px;
    left: 20px;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background-color: rgba(0, 0, 0, 0.3);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.25);
    z-index: 10;

    .icon {
      width: 24px;
      height: 24px;
    }
  }

  .icon {
    height: 14px;
    width: 12px;
    filter: drop-shadow(0 2px 4px rgba(color(white), 0.5));
  }

  .information {
    /deep/ {
      color: color(white);
      line-height: inherit;
      font-weight: 400;
      font-size: 12px;
      display: flex;
      align-items: center;
      width: fit-content;

      .label {
        display: none;
      }
    }
  }
}

@include desktop {
  .product-banner {
    padding-bottom: 0;
    padding-top: 0;
    display: flex;
    align-items: center;

    .page-title {
      margin: 0;
      margin-bottom: 12px;
    }

    h1 {
      font-size: 30px;
      line-height: 36px;
      padding: 0;
    }

    &.px20 {
      padding-left: 25%;
      padding-right: 85px;
    }

    .icon {
      height: 16px;
      width: 16px;

      &.mr10,
      &.mr5 {
        margin-right: 13px;
      }
    }

    .information-container {
      /deep/ {
        padding-bottom: 10px;
      }
    }

    .information {
      /deep/ {
        line-height: inherit;
        font-size: 20px;
      }

      &.mr15 {
        margin-right: 45px;
      }
    }

    .attribute-wrapper {
      .fs16 {
        font-size: 20px;
      }
    }
  }
}

@include mediaQuery(null, md) {
  .back-button {
    display: flex;
    align-items: center;
    justify-content: center;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }
}
</style>
